import React, { useState, useRef, useCallback } from 'react';
import { ReembolsoApi, CompraReembolsavel } from '../../../common/api/reembolso.api';
import { gerarTokenRecaptcha } from '../../../common/utils/getReCaptcha';
import * as S from './solicitar-reembolso.page.style';
import logoVendepay from '../../../assets/logo-full.png';

type Step = 'email' | 'compras' | 'codigo' | 'sucesso';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value: string): boolean => {
  if (!value) return false;
  return emailRegex.test(value.trim());
};

const reembolsoApi = new ReembolsoApi();

export const SolicitarReembolsoPage: React.FC = () => {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [compras, setCompras] = useState<CompraReembolsavel[]>([]);
  const [compraSelecionada, setCompraSelecionada] = useState<CompraReembolsavel | null>(null);
  const [codigo, setCodigo] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const codigoRefs = useRef<(HTMLInputElement | null)[]>([]);

  const getRecaptchaToken = useCallback(async (action: string) => {
    try {
      return await gerarTokenRecaptcha(action);
    } catch (err) {
      console.error('Erro ao obter token reCAPTCHA:', err);
      return 'mobile'; // Fallback para dev/mobile
    }
  }, []);

  const handleBuscarCompras = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError('Digite um e-mail válido.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const token = await getRecaptchaToken('/reembolso/buscar_compras');
      const response = await reembolsoApi.buscarCompras(email, token);

      if (response.error) {
        setError(response.message?.[0] || 'Erro ao buscar compras');
        return;
      }

      setCompras(response.data || []);
      setStep('compras');
    } catch (err: any) {
      const msg = err?.response?.data?.message?.[0] || err?.message || 'Erro ao buscar compras';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSolicitarReembolso = async (compra: CompraReembolsavel) => {
    setError('');
    setLoading(true);
    setCompraSelecionada(compra);

    try {
      const token = await getRecaptchaToken('/reembolso/enviar_codigo');
      const response = await reembolsoApi.enviarCodigo(compra.id, email, token);

      if (response.error) {
        setError(response.message?.[0] || 'Erro ao enviar código');
        setLoading(false);
        return;
      }

      setSuccessMessage('Código enviado para seu email!');
      setStep('codigo');
      setCodigo(['', '', '', '', '', '']);
    } catch (err: any) {
      const msg = err?.response?.data?.message?.[0] || err?.message || 'Erro ao enviar código';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCodigoChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCodigo = [...codigo];
    newCodigo[index] = value.slice(-1);
    setCodigo(newCodigo);

    if (value && index < 5) {
      codigoRefs.current[index + 1]?.focus();
    }
  };

  const handleCodigoPaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;

    const newCodigo = [...codigo];
    for (let i = 0; i < pasted.length && index + i < 6; i += 1) {
      newCodigo[index + i] = pasted[i];
    }
    setCodigo(newCodigo);

    const lastIndex = Math.min(index + pasted.length, 5);
    codigoRefs.current[lastIndex]?.focus();
  };

  const handleCodigoKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !codigo[index] && index > 0) {
      codigoRefs.current[index - 1]?.focus();
    }
  };

  const handleConfirmarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    const codigoCompleto = codigo.join('');

    if (codigoCompleto.length !== 6) {
      setError('Digite o código completo de 6 dígitos');
      return;
    }

    if (!compraSelecionada) {
      setError('Nenhuma compra selecionada');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const token = await getRecaptchaToken('/reembolso/confirmar_codigo');
      const response = await reembolsoApi.confirmarCodigo(
        compraSelecionada.id,
        codigoCompleto,
        token,
        1, // motivo padrão
        'Reembolso solicitado pelo comprador',
      );

      if (response.error) {
        setError(response.message?.[0] || 'Erro ao confirmar código');
        return;
      }

      setStep('sucesso');
    } catch (err: any) {
      const msg = err?.response?.data?.message?.[0] || err?.message || 'Código inválido ou expirado';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(valor);
  };

  const handleVoltar = () => {
    setError('');
    setSuccessMessage('');
    if (step === 'codigo') {
      setStep('compras');
      setCompraSelecionada(null);
    } else if (step === 'compras') {
      setStep('email');
      setCompras([]);
    }
  };

  return (
    <S.PageContainer>
      <S.Logo src={logoVendepay} alt="Vendepay" />

      <S.Card>
        {step === 'email' && (
          <>
            <S.Title>Solicitar reembolso</S.Title>
            <S.InfoText>
              Ao solicitar um reembolso, a sua compra será cancelada e você não terá mais acesso ao produto.
            </S.InfoText>

            <S.Form onSubmit={handleBuscarCompras}>
              <div>
                <S.Label htmlFor="email">E-mail usado na compra</S.Label>
                <S.Input
                  id="email"
                  type="email"
                  placeholder="Digite seu e-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {error && <S.ErrorMessage>{error}</S.ErrorMessage>}

              <S.Button type="submit" disabled={loading || !isValidEmail(email)}>
                {loading && <S.Spinner />}
                Encontrar sua compra
              </S.Button>
            </S.Form>
          </>
        )}

        {step === 'compras' && (
          <>
            <S.Title>Suas compras</S.Title>
            <S.Subtitle>Selecione a compra que deseja reembolsar</S.Subtitle>

            {compras.length === 0 ? (
              <S.EmptyState>
                <p>Nenhuma compra encontrada nos últimos 30 dias.</p>
                <p>Apenas compras pagas podem ser reembolsadas.</p>
              </S.EmptyState>
            ) : (
              <S.ComprasList>
                {compras.map((compra) => (
                  <S.CompraCard key={compra.id}>
                    <S.CompraInfo>
                      <S.CompraNome>{compra.nomeProduto}</S.CompraNome>
                      <S.CompraDetalhes>
                        Comprado em {formatarData(compra.dataCompra)} •{' '}
                        <S.CompraValor>{formatarValor(compra.valorPago)}</S.CompraValor>
                      </S.CompraDetalhes>
                      {compra.garantiaExpirada && (
                        <S.GarantiaExpiradaMsg>
                          Tempo de garantia expirado, para obter reembolso, entre em contato com {compra.nomeVendedor}.
                        </S.GarantiaExpiradaMsg>
                      )}
                    </S.CompraInfo>
                    {!compra.garantiaExpirada ? (
                      <S.ReembolsoButton
                        onClick={() => handleSolicitarReembolso(compra)}
                        disabled={loading}
                      >
                        {loading && compraSelecionada?.id === compra.id ? (
                          <S.Spinner />
                        ) : (
                          'Solicitar Reembolso'
                        )}
                      </S.ReembolsoButton>
                    ) : (
                      <S.ReembolsoButtonDisabled disabled>
                        Garantia Expirada
                      </S.ReembolsoButtonDisabled>
                    )}
                  </S.CompraCard>
                ))}
              </S.ComprasList>
            )}

            {error && <S.ErrorMessage>{error}</S.ErrorMessage>}

            <S.BackButton type="button" onClick={handleVoltar}>
              ← Voltar
            </S.BackButton>
          </>
        )}

        {step === 'codigo' && (
          <>
            <S.Title>Confirme seu reembolso</S.Title>
            <S.Subtitle>
              Enviamos um código de verificação para <strong>{email}</strong>
            </S.Subtitle>
            <S.InfoText>
              Digite o código de 6 dígitos abaixo para confirmar o reembolso de{' '}
              <strong>{compraSelecionada?.nomeProduto}</strong>
            </S.InfoText>
            <S.InfoText>
              O email com o código pode levar até 5 minutos para chegar. Não esqueça de verificar a caixa de spam.
            </S.InfoText>

            <S.Form onSubmit={handleConfirmarCodigo}>
              <S.CodeInputContainer>
                {codigo.map((digit, index) => (
                  <S.CodeInput
                    key={index}
                    ref={(el) => (codigoRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodigoChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodigoKeyDown(index, e)}
                    onPaste={(e) => handleCodigoPaste(index, e)}
                    autoFocus={index === 0}
                  />
                ))}
              </S.CodeInputContainer>

              {error && <S.ErrorMessage>{error}</S.ErrorMessage>}
              {successMessage && <S.SuccessMessage>{successMessage}</S.SuccessMessage>}

              <S.Button type="submit" disabled={loading || codigo.join('').length !== 6}>
                {loading && <S.Spinner />}
                Confirmar Reembolso
              </S.Button>
            </S.Form>

            <S.BackButton type="button" onClick={handleVoltar}>
              ← Voltar
            </S.BackButton>
          </>
        )}

        {step === 'sucesso' && (
          <S.SuccessContainer>
            <S.SuccessIcon>✓</S.SuccessIcon>
            <S.SuccessTitle>Reembolso solicitado!</S.SuccessTitle>
            <S.SuccessText>
              Sua solicitação de reembolso foi enviada com sucesso.
              <br />
              O valor será devolvido em até 7 dias úteis.
            </S.SuccessText>
            <S.Button
              variant="secondary"
              onClick={() => {
                setStep('email');
                setEmail('');
                setCompras([]);
                setCompraSelecionada(null);
                setCodigo(['', '', '', '', '', '']);
              }}
            >
              Solicitar outro reembolso
            </S.Button>
          </S.SuccessContainer>
        )}
      </S.Card>
    </S.PageContainer>
  );
};
